import React, { useEffect, useMemo, useState } from 'react';
import { Card, Typography } from 'antd';
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Cell,
    ReferenceLine,
} from 'recharts';
import { IProject } from 'models/project';
import YAxisLabelTwoLines from './YAxisLabelTwoLines';

const { Text } = Typography;

interface HarmonicDataPoint {
    harmonic: number;
    value: number;
}

interface MagnetomotiveForceHarmonicChartProps {
    project: IProject;
    maxHarmonics?: number;
}

// ── Style tokens (matching reference component) ───────────────────────────────
const TEXT_COLOR  = '#111827';
const GRID_COLOR  = '#E5E7EB';
const AXIS_STROKE = '#111827';

// ── Helpers ───────────────────────────────────────────────────────────────────

const formatHarmonic = (v: number): string => {
    if (!Number.isFinite(v)) return String(v);
    const rounded = Math.round(v * 1000) / 1000;
    return Number.isInteger(rounded) ? String(Math.round(rounded)) : rounded.toFixed(3);
};

const formatValue = (v: number): string => {
    if (!Number.isFinite(v)) return String(v);
    return Number.isInteger(v) ? String(v) : v.toFixed(1);
};

// ── Bar colors ────────────────────────────────────────────────────────────────
const COLOR_DEFAULT  = '#93c5fd'; // light blue
const COLOR_PEAK     = '#1890ff'; // strong blue — highest magnitude
const COLOR_SELECTED = '#f97316'; // orange — clicked bar

// ── Component ─────────────────────────────────────────────────────────────────

const MagnetomotiveForceHarmonicChart: React.FC<MagnetomotiveForceHarmonicChartProps> = ({
    project,
    maxHarmonics = 60,
}) => {
    const [selectedIdx, setSelectedIdx] = useState<number>(-1);

    // Reset on project change
    useEffect(() => {
        setSelectedIdx(-1);
    }, [project?.id]);

    // ── Data ──────────────────────────────────────────────────────────────────
    const data = useMemo<HarmonicDataPoint[]>(() => {
        const harmonic_x = project.results?.harmonic_order_x     ?? [];
        const harmonic_y = project.results?.harmonic_magnitude_y  ?? [];

        const n = Math.min(harmonic_x.length, harmonic_y.length);

        const points: HarmonicDataPoint[] = [];
        for (let i = 0; i < n; i++) {
            const raw = Number(harmonic_x[i] ?? i);
            const isNearInt = Number.isFinite(raw) && Math.abs(raw - Math.round(raw)) < 1e-9;
            points.push({
                harmonic: isNearInt ? Math.round(raw) : Math.round(raw * 1000) / 1000,
                value: harmonic_y[i] ?? 0,
            });
        }

        return points.slice(0, maxHarmonics);
    }, [project, maxHarmonics]);

    // Index of the highest-magnitude bar (the "fundamental")
    const peakIdx = useMemo(
        () => data.reduce((best, d, i) => (d.value > (data[best]?.value ?? -Infinity) ? i : best), 0),
        [data],
    );

    // ── Y-axis domain ─────────────────────────────────────────────────────────
    const yMax = useMemo(() => {
        if (!data.length) return 1;
        return Math.ceil(Math.max(...data.map(d => d.value)) * 10) / 10 * 1.15;
    }, [data]);

    // ── Bar click handler ─────────────────────────────────────────────────────
    const handleBarClick = (_: any, idx: number) => {
        setSelectedIdx(prev => (prev === idx ? -1 : idx)); // toggle
    };

    // ── Render ────────────────────────────────────────────────────────────────
    return (
        <Card style={{ width: '100%' }}>
            <Text strong style={{ fontSize: 16, color: TEXT_COLOR }}>Magnetomotive Force Harmonic</Text>

            <div style={{ width: '100%', height: 300, marginTop: 16 }}>
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                        data={data}
                        margin={{ top: 10, right: 10, bottom: 20, left: 40 }}
                        barCategoryGap={0}
                        barGap={0}
                    >
                        {/* Grid: solid lines, horizontal only — matches reference */}
                        <CartesianGrid
                            horizontal={true}
                            vertical={false}
                            stroke="#D1D5DB"
                            strokeOpacity={0.8}
                        />

                        {/* X-axis */}
                        <XAxis
                            dataKey="harmonic"
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            tick={false}
                            label={{
                                value: 'Harmonic order',
                                position: 'insideBottom',
                                dy: 14,
                                fill: AXIS_STROKE,
                                fontSize: 12,
                                textAnchor: 'middle',
                            }}
                        />

                        {/* Y-axis */}
                        <YAxis
                            width={60}
                            axisLine={{ stroke: GRID_COLOR }}
                            tickLine={false}
                            domain={[0, yMax]}
                            tick={{ fontSize: 12, fill: AXIS_STROKE }}
                            tickFormatter={formatValue}
                            label={{
                                content: (props: any) => (
                                    <YAxisLabelTwoLines
                                        {...props}
                                        lines={['Normalized', 'Magnetomotive Force']}
                                        fill={AXIS_STROKE}
                                        fontSize={12}
                                        textAnchor="middle"
                                    />
                                ),
                            }}
                        />

                        {/* Tooltip */}
                        <Tooltip
                            cursor={{ fill: 'rgba(24,144,255,0.06)' }}
                            content={({ active, payload }: any) => {
                                if (!active || !payload?.length) return null;
                                const { harmonic, value } = payload[0].payload as HarmonicDataPoint;
                                return (
                                    <div
                                        style={{
                                            background: '#ffffff',
                                            border: `1px solid ${GRID_COLOR}`,
                                            padding: 10,
                                            borderRadius: 10,
                                            boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
                                            fontSize: 12,
                                            color: TEXT_COLOR,
                                        }}
                                    >
                                        <div style={{ fontWeight: 700, marginBottom: 4 }}>
                                            Harmonic {formatHarmonic(harmonic)}
                                        </div>
                                        <div style={{ color: '#1890ff' }}>
                                            Value: {formatValue(value)}
                                        </div>
                                    </div>
                                );
                            }}
                        />

                        {/* Vertical reference line on selected bar */}
                        {selectedIdx >= 0 && data[selectedIdx] && (
                            <ReferenceLine
                                x={data[selectedIdx].harmonic}
                                stroke="#f97316"
                                strokeDasharray="4 2"
                                strokeWidth={1.5}
                            />
                        )}

                        <Bar dataKey="value" radius={[6, 6, 0, 0]} onClick={handleBarClick}>
                            {data.map((_, idx) => {
                                const color =
                                    idx === selectedIdx ? COLOR_SELECTED :
                                    idx === peakIdx     ? COLOR_PEAK     :
                                                          COLOR_DEFAULT;
                                return (
                                    <Cell
                                        key={`cell-${idx}`}
                                        fill={color}
                                        stroke={color}
                                        cursor="pointer"
                                        opacity={selectedIdx >= 0 && idx !== selectedIdx ? 0.6 : 1}
                                    />
                                );
                            })}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </Card>
    );
};

export default MagnetomotiveForceHarmonicChart;
